'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import type { PropertyDetailListing } from '../types';
import type { ApartmentReviews, BuildingReviewsSnapshot } from '@/src/lib/api/endpoints/apartmentReviews';
import { isApartmentReviewsEligible } from '@/src/lib/api/endpoints/apartmentReviews';
import { useApartmentReviews } from '@/src/lib/hooks/query/useApartmentReviews';
import { useFeatureFlag } from '@/src/lib/feature-flags';

const ReviewsContext = createContext<{
  lotId: string;
  reviews: ApartmentReviews | null;
  mockEnabled: boolean;
  integrationPreview: boolean;
  googlePlaceId?: string;
}>({
  lotId: '',
  reviews: null,
  mockEnabled: false,
  integrationPreview: false,
});

export function ApartmentReviewsProvider({
  listing,
  pdpType,
  snapshot,
  children,
}: {
  listing: PropertyDetailListing;
  pdpType: 'unit' | 'building';
  snapshot?: BuildingReviewsSnapshot;
  children: ReactNode;
}) {
  const reviews = useApartmentReviews(listing, pdpType, snapshot);
  const previewEnabled = useFeatureFlag('apartmentReviewsPreview');
  const eligible = isApartmentReviewsEligible(listing);
  const mockEnabled = previewEnabled && eligible;
  const integrationPreviewEnabled = useFeatureFlag('apartmentReviewsIntegrationPreview');
  const reviewsEnabled = useFeatureFlag('apartment-reviews');
  const integrationPreview = integrationPreviewEnabled && eligible;
  const googlePlaceId = integrationPreview ? 'ChIJ_QAalksVkFQRW4He3EDgWRM' : reviews?.google?.placeId;
  const lotId = listing.maloneLotIdEncoded ?? '';
  useEffect(() => {
    if (!integrationPreviewEnabled && !reviewsEnabled) return;
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (!cancelled)
        getGlobalMonitor()?.count({
          name: `apartment reviews google eligibility ${eligible ? 'eligible' : 'ineligible'}`,
        });
    });
    return () => {
      cancelled = true;
    };
  }, [lotId, eligible, integrationPreviewEnabled, reviewsEnabled]);
  return (
    <ReviewsContext.Provider value={{ lotId, reviews, mockEnabled, integrationPreview, googlePlaceId }}>
      {children}
    </ReviewsContext.Provider>
  );
}

export const useApartmentReviewsContext = () => useContext(ReviewsContext);
